import styled from '@emotion/styled';
import React, { useState, memo } from 'react';

import { withSpacingStyles } from '../withSpacingStyles';
import { ImageProvider } from './ImageProvider';

type Props = {
  className: string | null,
  uuid?: ?string,
  scope: 'grid' | 'list' | 'promo-banner' | 'promo-banner-mobile' | 'modifier',
  fallbackScope: 'grid' | 'list' | 'promo-banner' | 'promo-banner-mobile' | 'modifier',
  mode?: 'square',
  src?: ?string,
  srcSet?: ?(number[]),
  sizes?: ?string,
  alt: ?string,
  placeholder?: ?string,
  onError?: Function,
  width?: number,
  height?: number,
};

type DefaultImageProps = {
  uuid?: ?string,
  scope: 'grid' | 'list' | 'promo-banner' | 'promo-banner-mobile' | 'modifier',
  src?: ?string,
  srcSet?: ?(number[]),
  alt: ?string,
  placeholder?: ?string,
  loadError: boolean,
  externalSrc?: string,
  onErrorHandler: Function,
};

const DefaultImage = ({
  uuid,
  scope,
  src,
  srcSet,
  loadError,
  onErrorHandler,
  alt,
  placeholder,
  externalSrc,
  ...rest
}: DefaultImageProps) => {
  const shouldShowPlaceholder = loadError && placeholder;
  return (
    <ImageProvider uuid={uuid} scope={scope} src={src} srcSet={srcSet} externalSrc={externalSrc}>
      {({ imageSrc, srcSet }) => (
        <img
          onError={loadError ? undefined : onErrorHandler}
          alt={alt}
          src={shouldShowPlaceholder ? placeholder : imageSrc}
          srcSet={shouldShowPlaceholder ? null : srcSet}
          {...rest}
        />
      )}
    </ImageProvider>
  );
};

export const _Image = ({
  className,
  uuid,
  scope,
  fallbackScope,
  src,
  srcSet,
  alt,
  placeholder,
  onError,
  mode,
  width,
  height,
  ...rest
}: Props) => {
  const [loadError, setLoadError] = useState(false);
  const [currentScope, setScope] = useState(scope);

  const onErrorHandler = () => {
    setLoadError(true);
    onError && onError(true);
  };

  if (loadError && !placeholder) {
    if (fallbackScope && currentScope === scope) {
      setScope(fallbackScope);
      setLoadError(false);
    } else {
      return null;
    }
  }

  if (mode === 'square') {
    return (
      <SquarePicture className={className} width={width} height={height}>
        <SquareImage
          uuid={uuid}
          scope={currentScope}
          src={src}
          srcSet={srcSet}
          loadError={loadError}
          alt={alt}
          placeholder={placeholder}
          onErrorHandler={onErrorHandler}
          {...rest}
        />
      </SquarePicture>
    );
  }
  return (
    <DefaultImage
      className={className}
      uuid={uuid}
      scope={currentScope}
      src={src}
      srcSet={srcSet}
      loadError={loadError}
      alt={alt}
      placeholder={placeholder}
      onErrorHandler={onErrorHandler}
      {...rest}
    />
  );
};

export const Image = memo(withSpacingStyles(_Image));

const SquarePicture = styled('picture')`
  position: relative;
  display: flex;
  border-radius: ${({ theme }) => theme.borderRadius.large};
  overflow: hidden;
  padding-top: ${({ width }) => (width ? `${width}px` : '100%')};
  width: ${({ width }) => width && `${width}px`};
  height: ${({ width, height }) => (height ? `${height}px` : width ? `${width}px` : '')};
`;

const SquareImage = styled(DefaultImage)`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  margin: auto;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

Image.defaultProps = {
  scope: 'grid',
  className: null,
};
